import { type FC, useState } from "react";

import {
  Row,
  Column,
  Box,
  SectionHeading,
  Text,
  SearchInput,
} from "@hightouch/htui";
import { capitalize, orderBy, pickBy } from "lodash";

import { IntegrationBadge } from "src/components/integrations/integration-badge";
import { IntegrationIcon } from "src/components/integrations/integration-icon";
import { useUser } from "src/contexts/user-context";
import type {
  DestinationDefinitionFragment as DestinationDefinition,
  SourceCategory as SourceCategoryType,
  SourceDefinition,
} from "src/graphql";
import * as analytics from "src/lib/analytics";
import { newPylonMessage } from "src/lib/pylon";
import { Selectable } from "src/ui/selectable";
import { useFlags } from "launchdarkly-react-client-sdk";
import FuzzySet from "fuzzyset";

type ResourceDefinition = SourceDefinition | DestinationDefinition;

interface SourceCatalogProps {
  sampleDefinitions: SourceDefinition[];
  definitions: SourceDefinition[];
  selection?: SourceDefinition;
  onSelect: (definition: SourceDefinition) => void;
}

const filterSourceDefinitionsByInput = (
  definitions: SourceDefinition[],
  input: string,
) => {
  const definitionNames = definitions.map((def) => def.name);
  const fuzzyMatcher = FuzzySet(definitionNames);

  const matchedDefinitions = fuzzyMatcher.get(input, [], 0.7) || [];

  const matchedNames = new Set([
    ...matchedDefinitions.map((match) => match[1]),
    ...definitionNames.filter((name) =>
      name.toLowerCase().includes(input.toLowerCase()),
    ),
  ]);

  return definitions.filter((definition) => matchedNames.has(definition.name));
};

export const SourceCatalog: FC<Readonly<SourceCatalogProps>> = ({
  sampleDefinitions,
  definitions,
  selection,
  onSelect,
}) => {
  const [input, setInput] = useState<string>("");

  const filteredDefinitions = input
    ? filterSourceDefinitionsByInput(definitions, input)
    : definitions;

  const filteredSampleDefinitions = input
    ? filterSourceDefinitionsByInput(sampleDefinitions, input)
    : sampleDefinitions;

  const { categories } = useSourceCatalog(filteredDefinitions);

  const selectSource = (definition: SourceDefinition) => {
    analytics.track("Source Type Clicked", {
      source_type: definition.name,
      source_slug: definition.type,
      source_status: definition.status,
    });
    if (definition.status === "alpha" || definition.status === "coming_soon") {
      newPylonMessage(
        `Hi, I would like to participate in the private preview for the ${definition.name} source.`,
      );
    } else {
      onSelect(definition);
    }
  };

  return (
    <>
      <SearchInput
        placeholder="Search sources…"
        size="lg"
        width="100%"
        value={input}
        onChange={(e) => setInput(e.target.value)}
      />

      <Catalog
        sampleDefinitions={filteredSampleDefinitions}
        selection={selection}
        categories={categories}
        onSelect={selectSource}
      />
    </>
  );
};

type Categories = Omit<SourceCategoryType, "shortDescription" | "type"> & {
  shortDescription?: string;
  type?: string;
  definitions: SourceDefinition[];
};

type CategoryForCatalog<T extends ResourceDefinition> = {
  name: string;
  /** @default: true */
  capitalize?: boolean;
  shortDescription?: string;
  definitions: T[];
};

type CatalogProps<T extends ResourceDefinition> = {
  categories: Record<string, CategoryForCatalog<T>>;
  sampleDefinitions?: T[];
  selection?: T;
  onSelect: (definition: T) => void;
};

export function Catalog<T extends ResourceDefinition>({
  sampleDefinitions,
  selection,
  categories,
  onSelect,
}: Readonly<CatalogProps<T>>) {
  const categoryValues = Object.values(categories);
  return (
    <Column gap={6}>
      {!categoryValues.length && !sampleDefinitions?.length && (
        <Column align="center">
          <Text color="text.secondary">
            No results found. Please update your search and try again.
          </Text>
        </Column>
      )}
      {categoryValues.map((category) => (
        <SourceCategory
          key={category.name}
          name={category.name}
          capitalize={category.capitalize}
          description={category.shortDescription}
          selection={selection}
          definitions={category.definitions}
          onSelect={onSelect}
        />
      ))}
      {sampleDefinitions && sampleDefinitions?.length > 0 && (
        <SourceCategory
          name="Sample datasets"
          description="Try setting up your first sync using one of our sample datasets."
          selection={selection}
          definitions={sampleDefinitions}
          onSelect={onSelect}
        />
      )}
    </Column>
  );
}

type SourceCategoryProps<T extends ResourceDefinition> = {
  name: string;
  /** @default: true */
  capitalize?: boolean;
  description?: string | undefined;
  definitions: T[];
  selection?: T;
  onSelect: (definition: T) => void;
};

export function SourceCategory<T extends ResourceDefinition>({
  name,
  capitalize: capitalizeHeading = true,
  description,
  definitions,
  selection,
  onSelect,
}: Readonly<SourceCategoryProps<T>>) {
  return (
    <Box>
      <SectionHeading>
        {capitalizeHeading ? capitalize(name) : name}
      </SectionHeading>
      {description && <Text>{description}</Text>}
      <Box
        mt={5}
        display="grid"
        gridTemplateColumns={[
          "1fr",
          "repeat(2, 1fr)",
          "repeat(3, 1fr)",
          "repeat(3, 1fr)",
          "repeat(4, 1fr)",
        ]}
        gridGap={4}
      >
        {orderBy(definitions, ["name"], ["asc"]).map((definition) => (
          <Source<T>
            key={definition.type}
            definition={definition}
            selected={definition.type === selection?.type}
            onSelect={onSelect}
          />
        ))}
      </Box>
    </Box>
  );
}

type DefinitionCardProps<T extends ResourceDefinition> = {
  definition: T;
  onSelect: (definition: T) => void;
  selected: boolean;
};

export function Source<T extends ResourceDefinition>({
  definition,
  onSelect,
  selected,
}: Readonly<DefinitionCardProps<T>>) {
  return (
    <Selectable
      selected={selected}
      p={4}
      bg="white"
      overflow="visible"
      height="64px"
      onSelect={() => {
        onSelect(definition);
      }}
    >
      <Row align="center" gap={4} textAlign="left">
        <IntegrationIcon src={definition.icon} name={definition.name} />
        <Text fontWeight="medium">{definition.name}</Text>
      </Row>
      <Box pos="absolute" right={-2} top={-2}>
        {isSourceDefinition(definition) ? (
          <IntegrationBadge
            definition={definition}
            integrationType="source"
            isSample={definition.isSampleDataSource}
          />
        ) : (
          <IntegrationBadge
            definition={definition}
            integrationType="destination"
          />
        )}
      </Box>
    </Selectable>
  );
}

export const useSourceCatalog = (
  definitions: SourceDefinition[],
  released?: boolean,
) => {
  const { featureFlags } = useUser();
  const { appEnableGartnerDemo } = useFlags();

  const availableDefinitions = definitions.filter((definition) => {
    if (definition.type === "hubspotLegacy") {
      return (
        featureFlags?.hubspot_multi_merge_rule &&
        featureFlags?.hubspot_legacy_destination
      );
    }
    if (definition.status === "shadow") {
      return false;
    }
    if (definition.status === "deprecated") {
      return false;
    }
    if (released) {
      if (
        definition.status === "alpha" ||
        definition.status === "coming_soon"
      ) {
        return false;
      }
    }
    if (
      DATA_LAKEHOUSE_SOURCES.includes(definition.type) ||
      definition.categories?.some((c) => c?.name === "Data Lakehouses")
    ) {
      return appEnableGartnerDemo;
    }
    return true;
  });

  const categories: Record<string, Categories> = {
    "data systems": { definitions: [] } as any,
    ...(appEnableGartnerDemo
      ? {
          "data lakehouses": {
            definitions: [],
          } as any,
        }
      : {}),
    spreadsheets: { definitions: [] } as any,
    other: { definitions: [] } as any,
  };

  for (const definition of availableDefinitions) {
    const firstCategory = definition?.categories?.[0];

    if (firstCategory) {
      const name = firstCategory.name.toLowerCase();
      if (categories[name]) {
        categories[name] = {
          ...firstCategory,
          definitions: [...categories[name].definitions, definition],
        };
      }
    }
  }

  return {
    categories: pickBy(
      categories,
      (category) => category.definitions.length > 0,
    ),
    definitions: availableDefinitions,
  };
};

const isSourceDefinition = (
  definition: ResourceDefinition,
): definition is SourceDefinition => {
  return "isSampleDataSource" in definition;
};

const DATA_LAKEHOUSE_SOURCES = [
  "azure-datalake",
  "gcs-datalake",
  "azure-datalake",
];
